import { memo, type ReactNode } from 'react';
import type { Page } from '@rossum/api-client/pages';
import type { Prettify } from '../../../lib/typeUtils';
import { type CameraSlice, createStoreHooks } from '../../canvas/core';
import {
  PageContent,
  type PageLayoutData,
  type PageLayoutSlice,
  PagePositionTransform,
  usePageRenderData,
} from '../core';
import {
  PageCanvasOverlay,
  type PageCanvasOverlaySlice,
} from '../features/page-canvas-overlay';

type DocumentPageWrapperDeps = Prettify<
  CameraSlice & PageLayoutSlice & Partial<PageCanvasOverlaySlice>
>;

const { useStore } = createStoreHooks<DocumentPageWrapperDeps>();

export type DocumentPageWrapperProps = {
  pageModel: Page;
  layout: PageLayoutData;
  renderPageOverlays?: (pageModel: Page, layout: PageLayoutData) => ReactNode;
  children?: ReactNode;
};

const areLayoutsEqual = (a: PageLayoutData, b: PageLayoutData) =>
  a.x === b.x &&
  a.y === b.y &&
  a.width === b.width &&
  a.height === b.height &&
  a.pageId === b.pageId &&
  a.originalWidth === b.originalWidth &&
  a.originalHeight === b.originalHeight;

const arePropsEqual = (
  prev: DocumentPageWrapperProps,
  next: DocumentPageWrapperProps
) =>
  prev.pageModel === next.pageModel &&
  prev.renderPageOverlays === next.renderPageOverlays &&
  prev.children === next.children &&
  areLayoutsEqual(prev.layout, next.layout);

const DocumentPageWrapper = memo(
  ({
    pageModel,
    layout,
    renderPageOverlays,
    children,
  }: DocumentPageWrapperProps) => {
    const { pageState, contentBlob, isLoading, error, wrapWithTransforms } =
      usePageRenderData(pageModel, layout);

    const isActive = useStore(
      state => state.pageLayout.activeId === pageModel.id
    );

    // The page-canvas-overlay slice is optional: compositions that don't
    // compose the feature must not render its overlay.
    const hasPageCanvasOverlay = useStore(
      state => state.pageCanvasOverlay !== undefined
    );

    const pageContent = (
      <PageContent
        pageModel={pageModel}
        layout={layout}
        pageContentBlob={contentBlob}
        isLoading={isLoading}
        error={error}
        isActive={isActive}
      >
        {hasPageCanvasOverlay ? (
          <foreignObject
            x={0}
            y={0}
            width={layout.originalWidth}
            height={layout.originalHeight}
            style={{ overflow: 'visible', pointerEvents: 'none' }}
          >
            <PageCanvasOverlay pageLayout={layout} />
          </foreignObject>
        ) : null}
        {pageState.visible && renderPageOverlays?.(pageModel, layout)}
      </PageContent>
    );

    return (
      <PagePositionTransform layout={layout}>
        {children ?? wrapWithTransforms(pageContent)}
      </PagePositionTransform>
    );
  },
  arePropsEqual
);

DocumentPageWrapper.displayName = 'DocumentPageWrapper';

export { DocumentPageWrapper };
